/**
 * SttCompare (manager/admin) — blind STT engine A/B via a SINGLE-ELIMINATION
 * bracket, two transcripts at a time.
 *
 * Pick up to 5 engines → the server transcribes the SAME audio through each once
 * → the runs enter a bracket. Each "test" shows TWO blind transcripts (A vs B),
 * both time-synced to one audio player; you just pick the better one. Winners
 * advance until one champion remains (~N-1 tests for N engines). Reveal unmasks
 * the engines + the head-to-head leaderboard accumulates across calls.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { SttEngine, SttEvalBatch, SttEvalRun, SttReveal, SttSegment } from "@/types";
import { PageHeader, Button, Card, Badge } from "@/components/ui";
import { LoadingState } from "@/components/LoadingState";

const MAX_ENGINES = 5;

function clock(ms: number): string {
  const t = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

function callLabel(c: {
  started_at: string;
  direction: string;
  from_number: string | null;
  to_number: string | null;
  detected_rep_name: string | null;
  duration_sec: number;
}): string {
  const when = new Date(c.started_at).toLocaleString();
  const who = c.detected_rep_name ?? "—";
  const counter = c.direction === "Inbound" ? c.from_number : c.to_number;
  return `${when} · ${who} · ${counter ?? "unknown"} · ${clock(c.duration_sec * 1000)}`;
}

/** One blind transcript column: time-synced highlight + click-to-seek. */
function TranscriptColumn({
  run,
  currentMs,
  onSeek,
  reveal,
}: {
  run: SttEvalRun;
  currentMs: number;
  onSeek: (ms: number) => void;
  reveal: SttReveal | null;
}) {
  const segments: SttSegment[] = run.segments ?? [];
  const activeIdx = useMemo(() => {
    for (let i = 0; i < segments.length; i++) {
      if (currentMs >= segments[i]!.startMs && currentMs < segments[i]!.endMs) return i;
    }
    return -1;
  }, [segments, currentMs]);

  const activeRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  return (
    <Card className="flex min-h-0 flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold">{run.slot}</span>
        {run.status === "error" ? (
          <Badge tone="danger">error</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">{run.speaker_count ?? "?"} spk</span>
        )}
      </div>

      {reveal && (
        <div className="rounded bg-muted px-2 py-1 text-xs">
          <span className="font-semibold">{reveal.provider}</span> · {reveal.model}
          {reveal.latency_ms != null ? ` · ${(reveal.latency_ms / 1000).toFixed(1)}s` : ""}
          {reveal.cost_cents != null ? ` · ${reveal.cost_cents}¢` : ""}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto rounded border bg-background p-2 text-xs leading-relaxed">
        {run.status === "error" ? (
          <p className="text-red-600">{run.error ?? "transcription failed"}</p>
        ) : segments.length === 0 ? (
          <p className="text-muted-foreground">(no speech)</p>
        ) : (
          segments.map((s, i) => (
            <button
              key={i}
              ref={i === activeIdx ? activeRef : undefined}
              onClick={() => onSeek(s.startMs)}
              className={[
                "block w-full rounded px-1 py-0.5 text-left transition",
                i === activeIdx ? "bg-primary/15 font-medium" : "hover:bg-accent",
              ].join(" ")}
            >
              <span className="mr-1 text-[10px] uppercase text-muted-foreground">{s.speakerRaw ?? "?"}</span>
              {s.text}
            </button>
          ))
        )}
      </div>
    </Card>
  );
}

/* ── single-elimination bracket state machine ─────────────────────────────── */
interface BracketState {
  roundRuns: string[]; // run ids alive this round, in seed (slot) order
  pairIdx: number; // which pair of this round we're on
  winners: string[]; // winners collected so far this round
  round: number;
  testNo: number; // 1-based running test counter
  champion: string | null;
}

function initBracket(runIds: string[]): BracketState {
  return {
    roundRuns: runIds,
    pairIdx: 0,
    winners: [],
    round: 1,
    testNo: 1,
    champion: runIds.length <= 1 ? runIds[0] ?? null : null,
  };
}

function advanceBracket(s: BracketState, winnerRunId: string): BracketState {
  const numPairs = Math.floor(s.roundRuns.length / 2);
  const winners = [...s.winners, winnerRunId];
  if (s.pairIdx + 1 < numPairs) {
    return { ...s, pairIdx: s.pairIdx + 1, winners, testNo: s.testNo + 1 };
  }
  // end of round: winners advance; an odd leftover gets a bye into the next round
  const nextRound = [...winners];
  if (s.roundRuns.length % 2 === 1) nextRound.push(s.roundRuns[s.roundRuns.length - 1]!);
  if (nextRound.length <= 1) {
    return { ...s, winners, champion: nextRound[0] ?? null };
  }
  return { roundRuns: nextRound, pairIdx: 0, winners: [], round: s.round + 1, testNo: s.testNo + 1, champion: null };
}

export function SttCompare() {
  const qc = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [callId, setCallId] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modelByProvider, setModelByProvider] = useState<Record<string, string>>({});
  const [batch, setBatch] = useState<SttEvalBatch | null>(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [reveal, setReveal] = useState<Map<string, SttReveal> | null>(null);
  const [bracket, setBracket] = useState<BracketState | null>(null);

  const enginesQuery = useQuery({ queryKey: ["stt", "engines"], queryFn: ({ signal }) => api.sttEngines(signal) });
  const callsQuery = useQuery({ queryKey: ["stt", "calls"], queryFn: ({ signal }) => api.sttCalls(signal) });
  const aggQuery = useQuery({ queryKey: ["stt", "aggregate"], queryFn: ({ signal }) => api.sttAggregate(signal) });

  const engines: SttEngine[] = enginesQuery.data?.engines ?? [];

  const okRuns = useMemo(() => (batch?.runs ?? []).filter((r) => r.status === "ok"), [batch]);
  const runById = useMemo(() => new Map(okRuns.map((r) => [r.id, r])), [okRuns]);

  // Default-select the engines that have a usable key + seed their models.
  useEffect(() => {
    if (engines.length && selected.size === 0) {
      const withKey = engines.filter((e) => e.key_set).slice(0, MAX_ENGINES);
      if (withKey.length) {
        setSelected(new Set(withKey.map((e) => e.provider)));
        setModelByProvider((prev) => {
          const next = { ...prev };
          for (const e of withKey) if (!next[e.provider]) next[e.provider] = e.default_model;
          return next;
        });
      }
    }
  }, [engines, selected.size]);

  // Load any existing batch when the selected call changes.
  useEffect(() => {
    setBatch(null);
    setReveal(null);
    setBracket(null);
    setCurrentMs(0);
    if (!callId) return;
    let cancelled = false;
    api
      .sttEvalBatch(callId)
      .then((b) => {
        if (!cancelled) setBatch(b);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [callId]);

  // (Re)build the bracket whenever the set of successful runs changes.
  useEffect(() => {
    setBracket(okRuns.length ? initBracket(okRuns.map((r) => r.id)) : null);
  }, [okRuns]);

  const run = useMutation({
    mutationFn: () => {
      const variants = engines
        .filter((e) => selected.has(e.provider))
        .slice(0, MAX_ENGINES)
        .map((e) => ({
          provider: e.provider,
          model: (modelByProvider[e.provider] ?? e.default_model).trim() || e.default_model,
        }));
      return api.sttRun({ call_id: callId, variants });
    },
    onSuccess: (b) => {
      setBatch(b);
      setReveal(null);
      setCurrentMs(0);
    },
  });

  const recordMatchup = useMutation({
    mutationFn: (body: { run_a_id: string; run_b_id: string; winner_run_id: string; round: number }) =>
      api.sttMatchup({ call_id: callId, ...body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["stt", "aggregate"] }),
  });

  async function doReveal() {
    if (!callId) return;
    const { reveal: rows } = await api.sttReveal(callId);
    setReveal(new Map(rows.map((r) => [r.slot, r])));
  }

  function toggleEngine(provider: string) {
    const eng = engines.find((e) => e.provider === provider);
    if (selected.has(provider)) {
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(provider);
        return next;
      });
    } else if (selected.size < MAX_ENGINES) {
      setSelected((prev) => new Set(prev).add(provider));
      if (eng) setModelByProvider((m) => (m[provider] ? m : { ...m, [provider]: eng.default_model }));
    }
  }

  function setEngineModel(provider: string, model: string) {
    setModelByProvider((m) => ({ ...m, [provider]: model }));
  }

  function seek(ms: number) {
    const a = audioRef.current;
    if (a) {
      a.currentTime = ms / 1000;
      setCurrentMs(ms);
    }
  }

  // Current matchup = the two runs at the active pair of the active round.
  const matchup: [SttEvalRun, SttEvalRun] | null = useMemo(() => {
    if (!bracket || bracket.champion) return null;
    const a = runById.get(bracket.roundRuns[bracket.pairIdx * 2] ?? "");
    const b = runById.get(bracket.roundRuns[bracket.pairIdx * 2 + 1] ?? "");
    return a && b ? [a, b] : null;
  }, [bracket, runById]);

  function pick(winner: SttEvalRun, other: SttEvalRun) {
    if (!bracket) return;
    recordMatchup.mutate({ run_a_id: winner.id, run_b_id: other.id, winner_run_id: winner.id, round: bracket.round });
    setBracket((s) => (s ? advanceBracket(s, winner.id) : s));
  }

  const totalTests = Math.max(0, okRuns.length - 1);
  const championRun = bracket?.champion ? runById.get(bracket.champion) ?? null : null;
  const canRun = callId !== "" && selected.size > 0 && !run.isPending;

  return (
    <div className="flex flex-1 flex-col">
      <PageHeader
        title="STT compare & rate"
        subtitle="Blind, two-at-a-time tournament: pick the better transcript each round until one wins."
        actions={
          <Button onClick={() => void doReveal()} disabled={okRuns.length === 0} variant="secondary">
            {reveal ? "Revealed" : "Reveal engines"}
          </Button>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
        {/* Controls */}
        <Card className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-1 flex-col gap-1 text-sm">
              <span className="font-medium">Call</span>
              <select
                value={callId}
                onChange={(e) => setCallId(e.target.value)}
                className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select a recorded call…</option>
                {(callsQuery.data?.calls ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {callLabel(c)}
                  </option>
                ))}
              </select>
            </label>
            <Button onClick={() => run.mutate()} disabled={!canRun}>
              {run.isPending ? "Transcribing…" : okRuns.length ? "Re-run" : "Run"}
            </Button>
          </div>

          {/* Engine picker — toggle a vendor on, then choose its model */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              {engines.map((e) => {
                const checked = selected.has(e.provider);
                const atMax = !checked && selected.size >= MAX_ENGINES;
                const model = modelByProvider[e.provider] ?? e.default_model;
                const isCustom = checked && !e.models.includes(model);
                return (
                  <div
                    key={e.provider}
                    className={[
                      "flex items-center gap-1.5 rounded-md border px-2 py-1",
                      checked ? "border-primary/50 bg-primary/5" : "",
                    ].join(" ")}
                  >
                    <button
                      onClick={() => toggleEngine(e.provider)}
                      disabled={!e.key_set || atMax}
                      title={!e.key_set ? "No API key — add it in API Keys" : ""}
                      className={[
                        "text-xs font-medium transition disabled:opacity-40",
                        checked ? "text-primary" : "hover:underline",
                      ].join(" ")}
                    >
                      {checked ? "✓ " : "+ "}
                      {e.label}
                      {!e.key_set && " (no key)"}
                    </button>
                    {checked && (
                      <select
                        value={isCustom ? "__custom__" : model}
                        onChange={(ev) =>
                          setEngineModel(e.provider, ev.target.value === "__custom__" ? "" : ev.target.value)
                        }
                        className="rounded border bg-background px-1 py-0.5 text-[11px] outline-none focus:ring-1 focus:ring-ring"
                      >
                        {e.models.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                        <option value="__custom__">Custom…</option>
                      </select>
                    )}
                    {isCustom && (
                      <input
                        value={model}
                        onChange={(ev) => setEngineModel(e.provider, ev.target.value)}
                        placeholder="model id"
                        className="w-28 rounded border bg-background px-1 py-0.5 font-mono text-[11px] outline-none focus:ring-1 focus:ring-ring"
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <span className="text-xs text-muted-foreground">
              Pick up to {MAX_ENGINES} engines and a model for each. They all transcribe once, then you
              judge two at a time.
            </span>
          </div>

          {batch?.audio_url && !bracket?.champion && (
            <audio
              ref={audioRef}
              src={batch.audio_url}
              controls
              onTimeUpdate={(e) => setCurrentMs(e.currentTarget.currentTime * 1000)}
              className="w-full"
            />
          )}
          {run.isError && (
            <p className="text-sm text-red-600">Run failed — check that the selected engines have valid API keys.</p>
          )}
        </Card>

        {/* Bracket */}
        {run.isPending ? (
          <LoadingState label="Transcribing through each engine…" />
        ) : matchup ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                Test {bracket!.testNo} of {totalTests} · Round {bracket!.round}
              </span>
              <span className="text-xs text-muted-foreground">Pick the better transcript</span>
            </div>
            <div className="grid min-h-0 flex-1 grid-cols-2 gap-3">
              {matchup.map((r, i) => {
                const other = matchup[i === 0 ? 1 : 0]!;
                return (
                  <div key={r.id} className="flex min-h-0 flex-col gap-2">
                    <div className="min-h-0 flex-1">
                      <TranscriptColumn run={r} currentMs={currentMs} onSeek={seek} reveal={reveal?.get(r.slot) ?? null} />
                    </div>
                    <Button onClick={() => pick(r, other)} disabled={recordMatchup.isPending}>
                      {r.slot} is better
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : championRun ? (
          <Card className="flex flex-col items-center gap-3 py-8">
            <span className="text-sm text-muted-foreground">Winner of this call</span>
            <span className="text-3xl font-bold">{championRun.slot}</span>
            {reveal?.get(championRun.slot) ? (
              <span className="text-lg font-semibold">
                {reveal.get(championRun.slot)!.provider} · {reveal.get(championRun.slot)!.model}
              </span>
            ) : (
              <Button variant="secondary" onClick={() => void doReveal()}>
                Reveal which engine won
              </Button>
            )}
            <Button variant="ghost" onClick={() => setBracket(initBracket(okRuns.map((r) => r.id)))}>
              Re-run the bracket
            </Button>
          </Card>
        ) : okRuns.length === 0 && batch ? (
          <p className="text-sm text-muted-foreground">
            No successful transcripts to compare (all engines errored). Check API keys and re-run.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a call and engines, then Run to start the tournament.
          </p>
        )}

        {/* Head-to-head leaderboard */}
        <Card className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold">Head-to-head leaderboard</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="py-1 pr-3">Engine</th>
                  <th className="py-1 pr-3">Matchups</th>
                  <th className="py-1 pr-3">Wins</th>
                  <th className="py-1 pr-3">Win rate</th>
                  <th className="py-1 pr-3">🏆 Calls won</th>
                  <th className="py-1 pr-3">Avg latency</th>
                  <th className="py-1 pr-3">Avg cost</th>
                </tr>
              </thead>
              <tbody>
                {(aggQuery.data?.leaderboard ?? []).map((l) => (
                  <tr key={`${l.provider}:${l.model}`} className="border-t">
                    <td className="py-1 pr-3 font-medium">
                      {l.provider} <span className="text-muted-foreground">{l.model}</span>
                    </td>
                    <td className="py-1 pr-3">{l.matchups}</td>
                    <td className="py-1 pr-3">{l.wins}</td>
                    <td className="py-1 pr-3 font-semibold">{l.win_rate != null ? `${l.win_rate}%` : "—"}</td>
                    <td className="py-1 pr-3">{l.championships}</td>
                    <td className="py-1 pr-3">
                      {l.avg_latency_ms != null ? `${(l.avg_latency_ms / 1000).toFixed(1)}s` : "—"}
                    </td>
                    <td className="py-1 pr-3">{l.avg_cost_cents != null ? `${l.avg_cost_cents}¢` : "—"}</td>
                  </tr>
                ))}
                {(aggQuery.data?.leaderboard ?? []).length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-2 text-muted-foreground">
                      No matchups yet — run a tournament above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
